import React, { useEffect, useMemo, useRef, useState } from 'react';
import Ably from 'ably';
import { ChatClient, type ChatMessageEvent, type Room, type Message as AblyMessage, Message, OrderBy } from '@ably/chat';
import { getSupabaseClient } from '../lib/supabase';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatTimeInTimezone } from '../utils/timezoneUtils';

type UiMessage = {
  id: string;
  text: string;
  time: string;
  timestamp: number;
  fromMe?: boolean;
  serial?: string;
  deleted?: boolean;
};


type AvatarProps = {
  src?: string;
  alt: string;
  fallback?: string;
};

function Avatar({ src, alt, fallback }: AvatarProps) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#2C2731]/40 bg-[#E8DFBD]">
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <span className="text-sm font-medium text-[#2C2731]">{fallback}</span>
      )}
    </div>
  );
}

type SeekerSummary = {
  seekerId: string;
  authUserId?: string; // Auth user ID for notifications
  name: string;
  image?: string | null;
  status?: string;
};

type ConnectionState = 'connecting' | 'connected' | 'failed';

type GuideChatProps = {
  seeker: SeekerSummary;
  onBack?: () => void;
};

function getInitials(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return 'GC';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'GC';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const GuideChat: React.FC<GuideChatProps> = ({ seeker, onBack }) => {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState<ConnectionState>('connecting');
  const [occupancyCount, setOccupancyCount] = useState<number>(0);

  const supabase = useMemo(() => getSupabaseClient(), []);
  const { timezone: guideTimezone } = useGuideTimezone();

  const ablyRef = useRef<Ably.Realtime | null>(null);
  const chatClientRef = useRef<ChatClient | null>(null);
  const roomRef = useRef<Room | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const seekerImage = seeker.image ?? '';
  const hasSeekerImage = seekerImage.trim().length > 0;
  const seekerAvatarSrc = seekerImage;

  const seekerName = seeker.name?.trim() ? seeker.name : 'Seeker';
  const seekerInitials = useMemo(() => getInitials(seekerName), [seekerName]);

  // Clear message notifications on mount
  useEffect(() => {
    (async () => {
      try {
        // Get seeker's auth user ID to clear notifications
        if (seeker.authUserId) {
          try {
            await supabase.functions.invoke('clear-message-notification', {
              body: { sender_id: seeker.authUserId }
            });
          } catch (err) {
            console.warn('Failed to clear message notifications', err);
          }
        } else if (seeker.seekerId) {
          // Fetch auth_user_id from seekers table if not provided
          const { data: seekerData } = await supabase
            .from('seekers')
            .select('user_id')
            .eq('seeker_id', seeker.seekerId)
            .single();

          if (seekerData?.user_id) {
            try {
              await supabase.functions.invoke('clear-message-notification', {
                body: { sender_id: seekerData.user_id }
              });
            } catch (err) {
              console.warn('Failed to clear message notifications', err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch seeker data', err);
      }
    })();
  }, [supabase, seeker.authUserId, seeker.seekerId]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeConnection: (() => void) | undefined;
    let unsubscribeMessages: (() => void) | undefined;

    const serializeMessage = (
      input: ChatMessageEvent | Message
    ): UiMessage | null => {
      // History items are Message, live events are ChatMessageEvent
      const msg = 'message' in input ? input.message : input;

      if (!msg) return null;

      const timestamp = msg.timestamp ?? Date.now();

      // NEW: Format time in guide's timezone
      const timeStr = guideTimezone
        ? formatTimeInTimezone(new Date(timestamp).toISOString(), guideTimezone)
        : new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });

      return {
        id: msg.id ?? `${timestamp}-${Math.random()}`,
        text: msg.text ?? '',
        time: timeStr,
        timestamp,
        fromMe: msg.clientId?.startsWith('guide:'),
        serial: msg.serial,
        deleted: false,
      };
    };

    (async () => {
      try {
        setConnected('connecting');

        const { data: tokenPayload, error: tokenError } = await supabase.functions.invoke<{
          tokenRequest: Ably.Types.TokenRequest;
          roomName: string;
        }>('guide-chat-token', {
          body: { seeker_id: seeker.seekerId }
        });

        if (tokenError || !tokenPayload) {
          throw tokenError ?? new Error('Token request failed');
        }

        const { tokenRequest, roomName } = tokenPayload;
        if (cancelled) return;

        const ably = new Ably.Realtime({
          authCallback: (_params, cb) => cb(null, tokenRequest)
        });
        ablyRef.current = ably;

        const onConnectionChange = (state: Ably.Types.ConnectionStateChange) => {
          if (state.current === 'connected') {
            setConnected('connected');
          } else if (
            state.current === 'failed' ||
            state.current === 'closing' ||
            state.current === 'closed' ||
            state.current === 'suspended'
          ) {
            setConnected('failed');
          }
        };

        ably.connection.on(onConnectionChange);
        unsubscribeConnection = () => ably.connection.off(onConnectionChange);

        const chatClient = new ChatClient(ably);
        chatClientRef.current = chatClient;

        const room = await chatClient.rooms.get(roomName);
        if (cancelled) return;

        roomRef.current = room;
        await room.attach();

        try {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const history = await room.messages.history({
            start: sevenDaysAgo,
            orderBy: OrderBy.OldestFirst,
            limit: 200
          });
          if (!cancelled) {
            const historyMessages = history.items
              .map(serializeMessage)
              .filter((m): m is UiMessage => Boolean(m))
              .sort((a, b) => a.timestamp - b.timestamp);
            setMessages(historyMessages);
          }
        } catch (historyErr) {
          console.warn('Guide chat: failed to load history', historyErr);
        }

        const subscription = room.messages.subscribe((evt: ChatMessageEvent) => {
          const mapped = serializeMessage(evt);
          if (!mapped) return;
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === mapped.id)) {
              return prev;
            }
            const next = [...prev, mapped];
            next.sort((a, b) => a.timestamp - b.timestamp);
            return next;
          });
        });
        unsubscribeMessages = () => subscription.unsubscribe();

        await room.presence.enter({ role: 'guide' });
        if (!cancelled) {
          setConnected('connected');
        }
      } catch (err) {
        console.error('Guide chat initialisation failed', err);
        if (!cancelled) {
          setConnected('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeConnection?.();
      unsubscribeMessages?.();

      (async () => {
        try {
          await roomRef.current?.presence.leave();
        } catch (err) {
          console.warn('Guide chat: failed to leave presence', err);
        }
        try {
          if (roomRef.current) {
            await chatClientRef.current?.rooms.release(roomRef.current.name);
          }
        } catch (err) {
          console.warn('Guide chat: failed to release room', err);
        }
        try {
          ablyRef.current?.close();
        } catch (err) {
          console.warn('Guide chat: failed to close connection', err);
        }
        roomRef.current = null;
        chatClientRef.current = null;
        ablyRef.current = null;
      })();
    };
  }, [seeker.seekerId, supabase, guideTimezone]);

  // Poll room occupancy to determine online status
  useEffect(() => {
    if (!roomRef.current || connected !== 'connected') {
      console.log('[Guide Chat] Occupancy check skipped:', { hasRoom: !!roomRef.current, connected });
      return;
    }

    let cancelled = false;

    const pollOccupancy = async () => {
      // Don't proceed if component unmounted or connection closed
      if (cancelled || !roomRef.current || connected !== 'connected') {
        return;
      }

      try {
        // Use presence to count connections instead of occupancy API
        const presenceMembers = await roomRef.current?.presence.get();

        // Check again after async operation
        if (cancelled) return;

        console.log('[Guide Chat] Presence members:', presenceMembers);

        const connections = presenceMembers?.length ?? 0;
        setOccupancyCount(connections);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to get presence members', err);
        // If presence check fails, assume user is alone
        setOccupancyCount(1);
      }
    };

    // Poll immediately
    pollOccupancy();

    // Then poll every 5 seconds
    const intervalId = setInterval(pollOccupancy, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [connected]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    try {
      await roomRef.current?.messages.send({ text: trimmed });

      // If seeker is offline (fewer than 2 connections), send notification
      if (occupancyCount < 2) {
        try {
          // Get seeker's auth user ID for notification
          let receiverId = seeker.authUserId;

          if (!receiverId && seeker.seekerId) {
            // Fetch auth_user_id from seekers table
            const { data: seekerData } = await supabase
              .from('seekers')
              .select('user_id')
              .eq('seeker_id', seeker.seekerId)
              .single();

            receiverId = seekerData?.user_id;
          }

          if (receiverId) {
            await supabase.functions.invoke('send-message-notification', {
              body: { receiver_id: receiverId }
            });
          }
        } catch (notifErr) {
          console.warn('Failed to send message notification', notifErr);
        }
      }
    } catch (err) {
      console.error('Failed to send guide chat message', err);
      setInput(trimmed);
    }
  };

  const handleDeleteMessage = async (message: UiMessage) => {
    if (!message.serial || !roomRef.current) return;

    try {
      // Mark as deleted in UI immediately
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id ? { ...msg, deleted: true, text: 'Message deleted' } : msg
        )
      );

      // Delete from Ably
      await roomRef.current.messages.delete(message.serial, {
        description: 'Message deleted by user',
      });
    } catch (err) {
      console.error('Failed to delete message', err);
      // Revert the UI change on error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id ? { ...msg, deleted: false } : msg
        )
      );
    }
  };


  const messageNodes = useMemo(
    () =>
      messages.map((message) => {
        const isFromGuide = message.fromMe;
        const isDeleted = message.deleted;
        return (
          <div
            key={message.id}
            className={`flex items-start gap-3 ${isFromGuide ? 'flex-row-reverse' : ''}`}
          >
            <Avatar
              src={!isFromGuide && hasSeekerImage ? seekerAvatarSrc : undefined}
              alt={isFromGuide ? 'You' : seekerName}
              fallback={isFromGuide ? 'You' : seekerInitials}
            />
            <div className={`flex max-w-[314px] flex-col ${isFromGuide ? 'items-end' : ''}`}>
              <div
                className={`w-full rounded-lg px-4 py-3 text-sm shadow-sm ${
                  isDeleted
                    ? 'bg-[#2C2731]/10 text-[#2C2731]/40 italic'
                    : isFromGuide
                    ? 'bg-[#2C2731] text-[#FFFCEF]'
                    : 'bg-[#E8DFBD] text-[#2C2731]'
                }`}
              >
                {message.text}
              </div>
              <div className={`mt-2 flex items-center gap-2 text-xs ${
                isFromGuide ? 'text-[#2C2731]/60' : 'text-[#2C2731]/70'
              }`}>
                <span>{message.time}</span>
                {isFromGuide && !isDeleted && (
                  <>
                    <span>•</span>
                    <button
                      onClick={() => handleDeleteMessage(message)}
                      className="text-[#2C2731]/60 hover:text-[#2C2731] underline hidden"
                      title="Delete message"
                    >
                      delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      }),
    [hasSeekerImage, messages, seekerAvatarSrc, seekerInitials, seekerName]
  );

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          {onBack ? (
            <button
              type="button"
              aria-label="Close"
              className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
              onClick={onBack}
            >
              ×
            </button>
          ) : (
            <div className="w-10" aria-hidden />
          )}

          <div className="flex flex-1 items-center justify-center gap-3">
            <Avatar
              src={hasSeekerImage ? seekerAvatarSrc : undefined}
              alt={seekerName}
              fallback={seekerInitials}
            />
            <div className="flex flex-col">
              <span className="text-base font-medium text-[#FFFCEF] ph-mask">{seekerName}</span>
              <span className="text-xs text-[#FFFCEF]/70">
                {connected === 'connected'
                  ? occupancyCount >= 2
                    ? 'Online now'
                    : 'Offline'
                  : connected === 'connecting'
                  ? 'Connecting…'
                  : 'Offline'}
              </span>
            </div>
          </div>

          <div className="w-10" aria-hidden />
        </header>

        <main className="flex flex-1 min-h-0 flex-col">
          <div className="mx-auto flex h-full w-full max-w-2xl flex-1 min-h-0 flex-col gap-4 px-6 pb-6 pt-4">
          <div className="shrink-0 rounded-xl border border-[#2C2731] bg-[#E8DFBD] px-4 py-3 text-center text-sm font-medium text-[#2C2731] shadow-sm">
            Secure Messaging · Chat history kept for 7 days
          </div>

          <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-sm">
            <div ref={listRef} className="ph-no-capture flex-1 overflow-y-auto px-4 py-6">
              {connected === 'failed' ? (
                <p className="mx-auto mb-10 max-w-[324px] rounded-md border border-red-300 bg-red-100 px-3 py-2 text-center text-sm text-red-700">
                  We couldn&apos;t connect to chat. Please try again.
                </p>
              ) : !messages.length ? (
                <p className="mx-auto mb-10 max-w-[324px] text-center text-sm text-[#2C2731]/70">
                  Start the conversation to stay connected with your seeker.
                </p>
              ) : null}
              <div className="space-y-6">{messageNodes}</div>
            </div>

            <div className="border-t border-[#2C2731]/20 px-4 py-4 bg-[#FFFCEF]">
              <div className="flex items-center gap-3">
                <div className="flex-1 rounded-lg border border-[#2C2731] bg-[#E8DFBD] px-4 py-2.5">
                  <input
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="Type your message..."
                    className="w-full bg-transparent text-sm text-[#2C2731] placeholder:text-[#2C2731]/50 focus:outline-none"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    disabled={connected !== 'connected'}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={connected !== 'connected'}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#2C2731] bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF] disabled:opacity-50"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 2 11 13" />
                    <path d="m22 2-7 20-2-7-7-13z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        </main>
      </div>
    </div>
  );
};

export default GuideChat;
